import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  hasAcceptedRegulation,
  recordRegulationAcceptance,
  getStudentAcceptanceHistory,
  getLatestAcceptanceForResidence,
} from '@/backend/services/regulationAcceptance';
import { getActiveRegulation } from '@/backend/services/regulations';
import { getActiveContractByStudent } from '@/backend/services/contracts';

/**
 * Hook to check if student has accepted the current active regulation AND
 * acknowledged the rental contract. Returns regulation, contract, acceptance
 * status, and previous acceptance if exists. Used to detect re-acceptance
 * scenarios when regulations or the contract file change.
 */
export const useRegulationAcceptanceCheck = (
  studentId: string | undefined,
  residenceId: string | undefined
) => {
  return useQuery({
    queryKey: ['regulation-acceptance', studentId, residenceId],
    queryFn: async () => {
      if (!studentId || !residenceId) {
        return null;
      }

      const [activeRegulation, contract] = await Promise.all([
        getActiveRegulation(residenceId),
        getActiveContractByStudent(studentId),
      ]);

      if (!activeRegulation) {
        return null;
      }

      const hasAccepted = await hasAcceptedRegulation(
        studentId,
        activeRegulation.id
      );

      const previousAcceptance = await getLatestAcceptanceForResidence(
        studentId,
        residenceId
      );

      return {
        regulation: activeRegulation,
        contract,
        hasAccepted,
        previousAcceptance,
        isReAcceptance: previousAcceptance !== null && !hasAccepted,
      };
    },
    enabled: !!studentId && !!residenceId,
    staleTime: 0, // Always fetch fresh acceptance status
  });
};

/**
 * Hook to record regulation acceptance
 * Invalidates the acceptance check query on success
 */
export const useRecordRegulationAcceptance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      studentId,
      regulationId,
      regulationVersion,
      residenceId,
      contract,
    }: {
      studentId: string;
      regulationId: string;
      regulationVersion: string;
      residenceId: string;
      contract?: { contractId: string; contractFilePath: string };
    }) =>
      recordRegulationAcceptance(
        studentId,
        regulationId,
        regulationVersion,
        residenceId,
        contract
      ),
    onSuccess: (_, variables) => {
      // Invalidate acceptance check query for this student
      queryClient.invalidateQueries({
        queryKey: ['regulation-acceptance', variables.studentId],
      });

      // Also invalidate history query
      queryClient.invalidateQueries({
        queryKey: ['regulation-acceptance', variables.studentId, 'history'],
      });
    },
  });
};

/**
 * Hook to get student's acceptance history
 * Useful for displaying all regulations the student has accepted
 */
export const useStudentAcceptanceHistory = (studentId: string | undefined) => {
  return useQuery({
    queryKey: ['regulation-acceptance', studentId, 'history'],
    queryFn: () => getStudentAcceptanceHistory(studentId!),
    enabled: !!studentId,
  });
};
